import { AttendanceAggregatedBalance } from 'generated/resources/graphql'
import { computed, makeObservable } from 'mobx'

import { type AggregateBalanceByAccumulation } from 'modules/attendance/lib/utils/Balance'

import { CompensationCollection } from '../../hooks/useFetchCompensations'
import { BalancePool } from './BalancePool'
import { UniqueTimeBlockData } from './UniqueTimeBlockData'

export class TimeBlocksBalanceCollection {
  id: string | undefined
  dailyBalanceFromPlanning: string | undefined
  dailyBalanceFromContract: string | undefined
  employeeId: number | undefined
  startOn: string | undefined
  endOn: string | undefined
  balancePools: BalancePool[]
  compensations: CompensationCollection | undefined
  balancePayrollCompensationEnabled: boolean
  updatedAt: string | undefined

  constructor(
    timeBlock: AttendanceAggregatedBalance | AggregateBalanceByAccumulation | undefined,
    compensations: CompensationCollection | undefined = undefined,
    balancePayrollCompensationEnabled: boolean | undefined = false
  ) {
    this.id = timeBlock?.id
    this.dailyBalanceFromPlanning = timeBlock?.dailyBalanceFromPlanning.toString()
    this.dailyBalanceFromContract = timeBlock?.dailyBalanceFromContract.toString()
    this.employeeId = timeBlock?.employeeId
    this.startOn = timeBlock?.startOn
    this.endOn = timeBlock?.endOn
    this.updatedAt = timeBlock?.updatedAt
    this.balancePayrollCompensationEnabled = balancePayrollCompensationEnabled
    this.balancePools =
      timeBlock?.balancePools.map((pool) => new BalancePool(pool, compensations)) || []

    makeObservable(this, {
      payrollCompensationPendingAndCompensated: computed,
    })
  }

  get virtualPayrollPool(): BalancePool {
    const balancePool = this.balancePools.find((pool) => pool.type === 'virtual_payroll')

    return balancePool || BalancePool.empty('virtual_payroll')
  }

  get payrollPool(): BalancePool {
    const balancePool = this.balancePools.find((pool) => pool.type === 'payroll')

    return balancePool || BalancePool.empty('payroll')
  }

  get balancePool(): BalancePool {
    const balancePool = this.balancePools.find((pool) => pool.type === 'balance')

    return balancePool || BalancePool.empty('balance')
  }

  get virtualTimeoffPool(): BalancePool {
    const balancePool = this.balancePools.find((pool) => pool.type === 'virtual_timeoff')

    return balancePool || BalancePool.empty('virtual_timeoff')
  }

  get timeoffPool(): BalancePool {
    const balancePool = this.balancePools.find((pool) => pool.type === 'timeoff')

    return balancePool || BalancePool.empty('timeoff')
  }

  get discardedPool(): BalancePool {
    const balancePool = this.balancePools.find((pool) => pool.type === 'discarded')

    return balancePool || BalancePool.empty('discarded')
  }

  get timeoffCompensationPools() {
    return [this.virtualTimeoffPool, this.timeoffPool]
  }

  get timeOffTransfers() {
    const transfers: UniqueTimeBlockData[] = []
    this.timeoffCompensationPools.forEach((pool) => {
      transfers.push(...pool.transfers)
    })
    return transfers
  }

  get timeOffTransferMinutes() {
    return (
      this.timeoffCompensationPools.reduce((total, pool) => total + pool.totalTransferMinutes, 0) -
      this.timeoffCompensationPools.reduce((total, pool) => total + pool.discardedUsageMinutes, 0)
    )
  }

  get timeOffDiscardedTransfers() {
    const usages: UniqueTimeBlockData[] = []
    this.timeoffCompensationPools.forEach((pool) => {
      usages.push(...pool.usages.filter((usage) => usage.isDiscardedSource))
    })
    return usages
  }

  get payrollCompensationPools() {
    const pools = [this.virtualPayrollPool]
    if (this.balancePayrollCompensationEnabled) {
      pools.push(this.balancePool)
    }
    return pools
  }

  get payrollCompensationPendingAndCompensated() {
    let payrollPendingMinutes = 0
    let payrollCompensatedMinutes = 0
    const pendingPayrollBlocks: UniqueTimeBlockData[] = []
    const compensatedPayrollBlocks: UniqueTimeBlockData[] = []

    this.payrollCompensationPools.forEach((pool) => {
      const {
        pendingBlocks: pendingPoolBlocks,
        compensatedBlocks: compensatedPoolBlocks,
        pendingMinutes,
        compensatedMinutes,
      } = pool.pendingAndCompensated

      pendingPayrollBlocks.push(...pendingPoolBlocks)
      compensatedPayrollBlocks.push(...compensatedPoolBlocks)

      payrollPendingMinutes += pendingMinutes
      payrollCompensatedMinutes += compensatedMinutes
    })

    return {
      pendingPayrollBlocks,
      compensatedPayrollBlocks,
      payrollPendingMinutes,
      payrollCompensatedMinutes,
    }
  }

  get balancePoolTransfersMinutes(): number {
    return this.balancePool.transfers.reduce(
      (accumulator, current) => accumulator + current.minutes,
      0
    )
  }

  get balancePoolUsagesMinutes(): number {
    return this.balancePool.usages.reduce(
      (accumulator, current) => accumulator + current.minutes,
      0
    )
  }

  get balancePoolMinutes(): number {
    return this.balancePoolTransfersMinutes - this.balancePoolUsagesMinutes
  }

  get timeoffPoolTransfersMinutes(): number {
    return this.timeoffPool.transfers.reduce(
      (accumulator, current) => accumulator + current.minutes,
      0
    )
  }

  get discardedPoolTransfersMinutes(): number {
    return this.discardedPool.transfers.reduce(
      (accumulator, current) => accumulator + current.minutes,
      0
    )
  }

  get negativeBalance(): boolean {
    return this.balancePoolMinutes < 0
  }
}
